import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { AuthService } from '../services/services/auth.services';
import { ConfigService } from '../services/config.service';

// Marca interna para no reintentar una request que ya fue reintentada
const RETRY_HEADER = 'X-Auth-Retry';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);

  private configService = inject(ConfigService);

  /**
   * Intercepta requisições HTTP para adicionar o token de autenticação.
   * @param request - A requisição HTTP original.
   * @param next - O próximo handler na cadeia de interceptors.
   * @returns Observable do evento HTTP.
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {

    const apiUrl = this.configService.getCurrentApiUrl();
    const publicRoutes = [
      `${apiUrl}/login`,
      `${apiUrl}/refresh`,
      `${apiUrl}/token/refresh`
    ];


    if (publicRoutes.some(url => request.url.includes(url))) {
      return next.handle(request);
    }

    const token = this.authService.getAccessToken();
    const authRequest = token
      ? request.clone({
          setHeaders: { Authorization: `Bearer ${token}` }
        })
      : request;

    return next.handle(authRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        // Ante un 401 intentar renovar el access token y reintentar una vez
        const alreadyRetried = authRequest.headers.has(RETRY_HEADER);
        const canRefresh = !!this.authService.getRefreshToken();

        if (error.status !== 401 || alreadyRetried || !canRefresh) {
          return throwError(() => error);
        }

        return from(this.authService.refreshToken()).pipe(
          switchMap((newToken) => {
            if (!newToken) {
              return throwError(() => error);
            }
            const retried = authRequest.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`,
                [RETRY_HEADER]: '1',
              },
            });
            return next.handle(retried);
          }),
        );
      }),
    );
  }
}
